import { Box } from '@mui/material';
import React from 'react';
import { KitPill } from '../common/MobileKit';
import { ListBlock, PagedList } from './MobileReportLists';

/**
 * Peças do Relatório de Estoque no shell mobile (lista de movimentações e barra de nível).
 * Só apresentação: dados e formatação vêm de `pages/reports/StockReport.js`.
 */

const normalize = (value) => String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

// Mesmos tons de `StatusChip domain="stockMovement"`.
const movementTone = (type) => {
    switch (normalize(type)) {
        case 'entrada': return 'success';
        case 'saida': return 'danger';
        case 'estorno de venda': return 'warning';
        default: return undefined;
    }
};

const movementLabel = (type) => {
    switch (normalize(type)) {
        case 'entrada': return 'Entrada';
        case 'saida': return 'Saída';
        case 'cancelado': return 'Cancelado';
        case 'estorno de venda': return 'Estorno de venda';
        default: return type || '—';
    }
};

/**
 * - rows: movimentações já ordenadas
 * - getRow(row) -> { product, quantity, type, date, user, observations, quantityTone }
 */
export function MovementList({ rows, getRow, pageSize = 20 }) {
    return (
        <PagedList
            rows={rows}
            pageSize={pageSize}
            noun="movimentações"
            empty="Nenhuma movimentação no período"
            renderRow={(row) => {
                const r = getRow(row);
                return (
                    <ListBlock>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Box sx={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 14, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.product || '—'}</Box>
                            <Box sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.4, flexShrink: 0, color: r.quantityTone === 'success' ? 'success.main' : r.quantityTone === 'danger' ? 'error.main' : 'text.primary' }}>{r.quantity}</Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '6px' }}>
                            <KitPill tone={movementTone(r.type)}>{movementLabel(r.type)}</KitPill>
                            <Box sx={{ fontSize: 12, color: 'text.disabled', lineHeight: 1.4 }}>{r.date}</Box>
                        </Box>
                        {(r.user || r.observations) && (
                            <Box sx={{ fontSize: 12, color: 'text.disabled', mt: '6px', lineHeight: 1.4, overflowWrap: 'anywhere' }}>
                                {[r.user, r.observations].filter(Boolean).join(' · ')}
                            </Box>
                        )}
                    </ListBlock>
                );
            }}
        />
    );
}

// Barra de 3 zonas (baixo/médio/alto) com marcador — mesma lógica de `StockLevelPanel` do desktop.
export function StockLevelBar({ value }) {
    const level = Math.max(0, Math.min(100, Number(value) || 0));
    const band = level <= 20 ? 'Baixa' : level <= 55 ? 'Média' : 'Alta';
    return (
        <Box>
            <Box sx={{ height: 14, borderRadius: '8px', display: 'flex', backgroundColor: 'background.subtle', position: 'relative', mt: '6px' }}>
                <Box sx={{ width: '20%', backgroundColor: 'error.bg', borderRadius: '8px 0 0 8px' }} />
                <Box sx={{ width: '35%', backgroundColor: 'warning.bg' }} />
                <Box sx={{ width: '45%', backgroundColor: 'success.bg', borderRadius: '0 8px 8px 0' }} />
                <Box
                    sx={{
                        position: 'absolute',
                        top: -5,
                        width: 4,
                        height: 24,
                        borderRadius: '3px',
                        backgroundColor: 'text.primary',
                        boxShadow: (t) => `0 0 0 3px ${t.palette.background.paper}`,
                        left: `calc(${level}% - 2px)`,
                    }}
                />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'text.disabled', fontWeight: 600, mt: '8px', lineHeight: 1.4 }}>
                <span>Baixo</span>
                <span>Médio</span>
                <span>Alto</span>
            </Box>
            <Box sx={{ fontSize: 12, color: 'text.secondary', mt: '8px', lineHeight: 1.4 }}>
                Estoque atual está na faixa <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{band}</Box> — {level.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% da capacidade de referência.
            </Box>
        </Box>
    );
}
